import { Target } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import {
  Create,
  Confirm,
  useDataProvider,
  useSaveContext,
  Datagrid,
  Edit,
  List,
  SaveButton,
  SimpleForm,
  TextField,
  TextInput,
  SelectInput,
  NumberInput,
  BooleanInput,
  BooleanField,
  ReferenceInput,
  ReferenceField,
  AutocompleteInput,
  AutocompleteArrayInput,
  DateTimeInput,
  DateField,
  Toolbar,
  required,
  useNotify,
  useRedirect,
  useRefresh,
  useRecordContext,
  useGetOne,
  useGetList,
  useGetMany,
  useCreate,
  useUpdate,
  useListContext,
  WithListContext,
  minValue,
} from "react-admin";
import { useWatch, useFormContext } from "react-hook-form";
import { Button, FormControlLabel, Checkbox } from "@mui/material";
import { CardListActions, SpeakerInput } from "../../components";
import {
  filterByName,
  formatDateTimeInputForMysql,
  parseDateTimeInputForMysql,
} from "../../components/utils";

const PageDescription = () => (
  <div className="page-description">
    <h1>Ad Placements</h1>
    <p>
      Manage ad placements that connect assets to locations with specific
      targeting and scheduling rules.
    </p>
  </div>
);

const AddEditPageDescription = () => (
  <div className="page-description">
    <h1>Add or Edit Ad Placement</h1>
    <p>
      Configure an ad placement with its asset, location, visibility rules,
      scheduling, and frequency capping.
    </p>
  </div>
);

const frequencyCapStorageChoices = [
  { id: "local", name: "Local Storage (persistent)" },
  { id: "session", name: "Session Storage (per session)" },
];

/** Custom Input which only renders the `visibility_speaker_id` filter when the selected `location_id` filter is a speaker page */
function SpeakerFilterInput(props) {
  const { filterValues, displayedFilters, setFilters } = useListContext();
  const locationId = filterValues.location_id;
  const { data: location, isLoading } = useGetOne(
    "ad_locations",
    { id: locationId },
    { enabled: !!locationId },
  );
  const isSpeakerLocation = !!location?.slug?.includes("speaker");

  // Hiding the input would otherwise leave its value applied to the list with
  // no way to see or clear it, so drop the speaker once the location can no
  // longer target one. The slug arrives async: wait for it, or a filter
  // restored from the URL is cleared before its location resolves.
  useEffect(() => {
    const isResolvingLocation = !!locationId && isLoading;
    if (isResolvingLocation || isSpeakerLocation) return;
    if (filterValues.visibility_speaker_id === undefined) return;

    const { visibility_speaker_id: _, ...rest } = filterValues;
    setFilters(rest, displayedFilters);
  }, [
    locationId,
    isLoading,
    isSpeakerLocation,
    filterValues,
    displayedFilters,
    setFilters,
  ]);

  if (!isSpeakerLocation) return null;

  return <SpeakerInput {...props} />;
}

const filters = [
  <WithListContext
    key="active_only"
    alwaysOn
    render={({ setFilters, filterValues }) => {
      const isActive = filterValues["starts_at.<"] && filterValues["ends_at.>"];
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!isActive}
              onChange={(e) => {
                if (e.target.checked) {
                  setFilters({
                    ...filterValues,
                    "starts_at.<": new Date().toISOString(),
                    "ends_at.>": new Date().toISOString(),
                  });
                } else {
                  const {
                    "starts_at.<": _,
                    "ends_at.>": __,
                    ...rest
                  } = filterValues;
                  setFilters(rest);
                }
              }}
            />
          }
          label="Active Only"
        />
      );
    }}
  />,
  <TextInput source="id" label="ID" key="id" alwaysOn />,
  <TextInput
    source="asset_title"
    label="Asset Title"
    key="asset_title"
    alwaysOn
  />,
  <ReferenceInput
    source="location_id"
    reference="ad_locations"
    label="Location"
    key="location"
    alwaysOn
  >
    <AutocompleteInput optionText="slug" />
  </ReferenceInput>,
  <SelectInput
    source="is_filler"
    label="Is Filler"
    choices={[
      { id: true, name: "Yes" },
      { id: false, name: "No" },
    ]}
    key="is_filler"
    alwaysOn
  />,
  <SpeakerFilterInput
    label="Speaker"
    source="visibility_speaker_id"
    key="visibility_speaker_id"
    alwaysOn
  />,
];

// Display asset with preview thumbnail
const AssetPreviewField = () => {
  const record = useRecordContext();
  if (!record) return null;

  let preview = null;
  if (record.type === "image" && record.content_default) {
    preview = (
      <img
        src={record.content_default}
        alt="Preview"
        style={{
          maxWidth: 60,
          maxHeight: 40,
          marginRight: 8,
          verticalAlign: "middle",
        }}
      />
    );
  } else if (record.type === "video") {
    preview = <span style={{ marginRight: 8 }}>🎬</span>;
  } else if (record.type === "html") {
    preview = <span style={{ marginRight: 8 }}>📄</span>;
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      {preview}
      <span>{record.title || `#${record.id} (${record.type})`}</span>
    </span>
  );
};

// Preview component for selected asset in Edit/Create form with dimension detection
const SelectedAssetPreview = () => {
  const assetId = useWatch({ name: "asset_id" });
  const { data: asset, isLoading } = useGetOne(
    "ad_assets",
    { id: assetId },
    { enabled: !!assetId },
  );
  const [dimensions, setDimensions] = useState(null);

  useEffect(() => {
    if (asset?.type === "image" && asset?.content_default) {
      const img = new Image();
      img.onload = () => {
        setDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
          ratio: (img.naturalWidth / img.naturalHeight).toFixed(2),
        });
      };
      img.onerror = () => {
        setDimensions(null);
      };
      img.src = asset.content_default;
    } else {
      setDimensions(null);
    }
  }, [asset]);

  if (!assetId) return null;
  if (isLoading)
    return (
      <div style={{ marginBottom: 16, color: "#666" }}>Loading preview...</div>
    );
  if (!asset) return null;

  let preview = null;
  if (asset.type === "image" && asset.content_default) {
    preview = (
      <img
        src={asset.content_default}
        alt={asset.title || "Asset preview"}
        style={{ maxWidth: 300, maxHeight: 200, objectFit: "contain" }}
      />
    );
  } else if (asset.type === "video" && asset.content_default) {
    preview = (
      <video
        src={asset.content_default}
        style={{ maxWidth: 300, maxHeight: 200 }}
        controls
        muted
      />
    );
  } else if (asset.type === "html" && asset.content_default) {
    preview = (
      <div
        style={{
          maxWidth: 300,
          maxHeight: 200,
          overflow: "auto",
          border: "1px solid #ccc",
          padding: 8,
          background: "#f9f9f9",
        }}
        dangerouslySetInnerHTML={{ __html: asset.content_default }}
      />
    );
  }

  if (!preview) return null;

  return (
    <div
      style={{
        marginBottom: 16,
        padding: 12,
        background: "#f5f5f5",
        borderRadius: 4,
      }}
    >
      <h4 style={{ marginBottom: 8, marginTop: 0 }}>Selected Asset</h4>
      {preview}
      {dimensions && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#333" }}>
          <strong>Asset Dimensions:</strong> {dimensions.width}x
          {dimensions.height} (ratio: {dimensions.ratio})
        </div>
      )}
      {asset.type !== "image" && (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            color: "#666",
            fontStyle: "italic",
          }}
        >
          Dimensions not available for {asset.type} assets
        </div>
      )}
    </div>
  );
};

// Display selected location info with preview image and dimensions
const SelectedLocationInfo = () => {
  const locationId = useWatch({ name: "location_id" });
  const { data: location, isLoading } = useGetOne(
    "ad_locations",
    { id: locationId },
    { enabled: !!locationId },
  );

  if (!locationId) return null;
  if (isLoading)
    return (
      <div style={{ marginBottom: 16, color: "#666" }}>
        Loading location info...
      </div>
    );
  if (!location) return null;

  // Parse dimensions (e.g., "1200x400" -> { width: 1200, height: 400 })
  const parseDimensions = (dimStr) => {
    if (!dimStr) return null;
    const match = dimStr.match(/^(\d+)x(\d+)$/);
    if (!match) return null;
    const w = parseInt(match[1], 10);
    const h = parseInt(match[2], 10);
    return { width: w, height: h, ratio: (w / h).toFixed(2) };
  };

  const defaultDims = parseDimensions(location.dimensions_default);
  const mobileDims = parseDimensions(location.dimensions_mobile);

  return (
    <div
      style={{
        marginBottom: 16,
        padding: 12,
        background: "#f5f5f5",
        borderRadius: 4,
      }}
    >
      <h4 style={{ marginBottom: 8, marginTop: 0 }}>
        Location: {location.slug}
      </h4>
      {location.preview_image_url && (
        <div style={{ marginBottom: 8 }}>
          <img
            src={location.preview_image_url}
            alt="Location preview"
            style={{ maxWidth: 400, maxHeight: 250, border: "1px solid #ddd" }}
          />
        </div>
      )}
      <div style={{ fontSize: 13, color: "#333" }}>
        <strong>Expected Dimensions:</strong>
        {defaultDims && (
          <div>
            Desktop: {defaultDims.width}x{defaultDims.height} (ratio:{" "}
            {defaultDims.ratio})
          </div>
        )}
        {mobileDims && (
          <div>
            Mobile: {mobileDims.width}x{mobileDims.height} (ratio:{" "}
            {mobileDims.ratio})
          </div>
        )}
        {!defaultDims && !mobileDims && (
          <span style={{ color: "#999" }}> Not specified</span>
        )}
      </div>
    </div>
  );
};

// Status indicator based on scheduling
const StatusField = ({ record }) => {
  if (!record) return null;

  const now = new Date();
  const startsAt = record.starts_at ? new Date(record.starts_at) : null;
  const endsAt = record.ends_at ? new Date(record.ends_at) : null;

  if (startsAt && startsAt > now) {
    return <span style={{ color: "orange" }}>⏰ Scheduled</span>;
  }
  if (endsAt && endsAt < now) {
    return <span style={{ color: "gray" }}>⏹️ Ended</span>;
  }
  return <span style={{ color: "green" }}>✅ Active</span>;
};

/** Speaker banners use the speaker's on/off toggle rather than booked dates. */
const RunDateField = ({ source }) => {
  const record = useRecordContext();
  const { data: location } = useGetOne(
    "ad_locations",
    { id: record?.location_id },
    { enabled: !!record?.location_id },
  );
  if (location?.slug?.includes("speaker")) return <span>—</span>;
  return <DateField source={source} showTime />;
};

/** All visible rows share one react-admin query for lifetime placement counts.
 * The main list stays available while this independent grouped request loads. */
export const PlacementStatField = ({ source }) => {
  const record = useRecordContext();
  const { data: placements = [] } = useListContext();
  const placementIds = placements.map(({ id }) => id).sort((a, b) => a - b);
  const { data, isLoading, error } = useGetList(
    "event_counts",
    {
      filter: {
        eventName: ["system.ad.impression", "user.ad.click"],
        "payload.placement_id.:Int64": placementIds,
        groupBy: "payload.placement_id.:Int64,eventName",
      },
      pagination: { page: 1, perPage: Math.max(1, placementIds.length * 2) },
      sort: { field: "id", order: "ASC" },
    },
    {
      enabled: placementIds.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
  if (isLoading) return <span aria-label="Loading stats">…</span>;
  if (error)
    return <span title="Stats could not be loaded. Refresh to retry.">—</span>;

  const counts = data?.filter(
    (row) => row["payload.placement_id.:Int64"] === Number(record.id),
  );
  const impressions =
    counts?.find((row) => row.eventName === "system.ad.impression")?.count ?? 0;
  const clicks =
    counts?.find((row) => row.eventName === "user.ad.click")?.count ?? 0;
  if (source === "ctr") {
    return (
      <span>
        {impressions ? `${((clicks / impressions) * 100).toFixed(2)}%` : "—"}
      </span>
    );
  }
  return (
    <span>{(source === "clicks" ? clicks : impressions).toLocaleString()}</span>
  );
};

function ListView(props) {
  const rowSx = (record) => {
    const now = new Date();
    const startsAt = record.starts_at ? new Date(record.starts_at) : null;
    const endsAt = record.ends_at ? new Date(record.ends_at) : null;
    const isActive =
      (!startsAt || startsAt <= now) && (!endsAt || endsAt > now);

    if (isActive) {
      return {
        backgroundColor: "rgba(76, 175, 80, 0.06)",
        "&:hover": {
          backgroundColor: "rgba(76, 175, 80, 0.12) !important",
        },
      };
    }
    return {};
  };

  return (
    <>
      <PageDescription />
      <List
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        perPage={50}
        sort={{ field: "created_at", order: "DESC" }}
        title="Ad Placements"
      >
        <Datagrid rowClick="edit" rowSx={rowSx}>
          <TextField source="id" label="ID" />
          {/* Disable the asset reference link so clicks edit the placement via the row. */}
          <ReferenceField
            source="asset_id"
            reference="ad_assets"
            label="Asset"
            link={false}
          >
            <AssetPreviewField />
          </ReferenceField>
          <ReferenceField
            source="location_id"
            reference="ad_locations"
            label="Location"
            link={false}
          >
            <TextField source="slug" />
          </ReferenceField>
          <BooleanField source="is_filler" label="Filler?" />
          <StatusField label="Status" />
          <TextField source="target_url" label="Target URL" />
          <RunDateField source="starts_at" label="Starts" />
          <RunDateField source="ends_at" label="Ends" />
          <PlacementStatField source="clicks" label="Clicks" sortable={false} />
          <PlacementStatField
            source="impressions"
            label="Impressions"
            sortable={false}
          />
          <PlacementStatField source="ctr" label="CTR" sortable={false} />
        </Datagrid>
      </List>
    </>
  );
}

/** Keep an unrestricted audience null in form state, so ReferenceInput never
 * tries to fetch a synthetic "None" speaker ID. Both None and clearing are optional. */
export const PlacementSpeakerInput = () => (
  <ReferenceInput source="visibility_speaker_id" reference="speakers">
    <AutocompleteInput
      label="Speaker-Specific"
      optionText={(record) =>
        record?.id === ""
          ? "None"
          : record
            ? `${record.name_first} ${record.name_last}`
            : ""
      }
      emptyText="None"
      format={(value) => value ?? ""}
      parse={(value) => (value === "" ? null : value)}
      helperText="Only show this ad on pages for this speaker. None removes this restriction."
    />
  </ReferenceInput>
);

/** None is a form-only choice: exclude it from speaker lookups and strip it on
 * save. This keeps an explicit choice required without inventing a speaker ID. */
const FeaturedSpeakersInput = () => {
  const [search, setSearch] = useState("");
  const selectedIds = useWatch({ name: "featured_speaker_ids" }) || [];
  const speakerIds = selectedIds.filter((id) => id !== "none");
  const { data: speakers = [], isLoading } = useGetList("speakers", {
    filter: filterByName(search),
    pagination: { page: 1, perPage: 25 },
    sort: { field: "id", order: "ASC" },
  });
  const { data: selected = [] } = useGetMany(
    "speakers",
    { ids: speakerIds },
    { enabled: speakerIds.length > 0 },
  );
  return (
    <AutocompleteArrayInput
      source="featured_speaker_ids"
      label="Featured speaker(s)"
      choices={[
        { id: "none", name: "None" },
        ...selected,
        ...speakers.filter((speaker) => !speakerIds.includes(speaker.id)),
      ]}
      optionText="name"
      setFilter={setSearch}
      isLoading={isLoading}
      parse={(ids) =>
        ids[ids.length - 1] === "none"
          ? ["none"]
          : ids.filter((id) => id !== "none")
      }
      validate={required()}
      fullWidth
      helperText="Choose None or the speakers who appear in this banner. With speakers selected, it is hidden on other speakers' pages, but still runs everywhere else."
    />
  );
};

const AdPlacementFormFields = () => {
  return (
    <>
      <AddEditPageDescription />

      <h4>Asset & Location</h4>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <SelectedAssetPreview />
        <SelectedLocationInfo />
      </div>
      <ReferenceInput
        source="asset_id"
        reference="ad_assets"
        label="Asset"
        validate={[required()]}
      >
        <AutocompleteInput
          filterToQuery={(searchText) => ({ title: searchText })}
          optionText={(record) =>
            record
              ? record.title
                ? `${record.title} (#${record.id})`
                : `#${record.id} (${record.type})`
              : ""
          }
        />
      </ReferenceInput>
      <ReferenceInput
        source="location_id"
        reference="ad_locations"
        label="Location"
        validate={[required()]}
      >
        <AutocompleteInput optionText="slug" />
      </ReferenceInput>

      <h4>Basic Settings</h4>
      <BooleanInput
        source="is_filler"
        label="Is Filler Ad"
        helperText="Filler ads are shown when there aren't enough regular ads"
      />
      <FeaturedSpeakersInput />
      <NumberInput
        source="sticky_position"
        label="Sticky Position"
        helperText="Fixed position in the ad sequence (1-indexed). Leave empty for non-sticky."
        validate={[minValue(1)]}
      />
      <NumberInput
        source="cell_span"
        label="Cell Span"
        defaultValue={1}
        helperText="Number of cells this ad spans in a grid layout"
      />
      <TextInput
        source="target_url"
        label="Target URL"
        fullWidth
        helperText="URL to navigate to when the ad is clicked"
      />

      <h4>Visibility Rules</h4>
      <BooleanInput
        source="visibility_female_only"
        label="Female Users Only"
        helperText="Only show this ad to female users"
      />
      <BooleanInput
        source="visibility_logged_in_only"
        label="Logged In Users Only"
        helperText="Only show this ad to logged-in users"
      />
      <BooleanInput
        source="visibility_not_logged_in_only"
        label="Non-Logged In Users Only"
        helperText="Only show this ad to users who are not logged in"
      />
      <PlacementSpeakerInput />

      <h4>Scheduling</h4>
      <DateTimeInput
        source="starts_at"
        label="Start Date/Time"
        helperText="Ad becomes active at this time (leave empty for immediate)"
        parse={parseDateTimeInputForMysql}
        format={formatDateTimeInputForMysql}
      />
      <DateTimeInput
        source="ends_at"
        label="End Date/Time"
        helperText="Ad becomes inactive after this time (leave empty for no end)"
        parse={parseDateTimeInputForMysql}
        format={formatDateTimeInputForMysql}
      />

      <h4>Frequency Capping</h4>
      <NumberInput
        source="frequency_cap_seconds"
        label="Frequency Cap (seconds)"
        helperText="Minimum seconds between showing this ad to the same user (leave empty for no cap)"
      />
      <SelectInput
        source="frequency_cap_storage"
        label="Frequency Cap Storage"
        choices={frequencyCapStorageChoices}
        defaultValue="local"
        helperText="Where to store the frequency cap (local = persistent, session = per browser session)"
      />
    </>
  );
};

// Fields that are considered "content" fields (not scheduling) for dirty detection
const CONTENT_FIELDS = [
  "asset_id",
  "location_id",
  "is_filler",
  "sticky_position",
  "cell_span",
  "target_url",
  "visibility_female_only",
  "visibility_logged_in_only",
  "visibility_not_logged_in_only",
  "visibility_speaker_id",
  "featured_speaker_ids",
  "frequency_cap_seconds",
  "frequency_cap_storage",
];

// Hook to detect if any content fields (non-scheduling) have changed
const useContentFieldsDirty = () => {
  const record = useRecordContext();
  // Watch all content fields individually (safe because CONTENT_FIELDS is a static constant)
  /* eslint-disable react-hooks/rules-of-hooks */
  const asset_id = useWatch({ name: "asset_id" });
  const location_id = useWatch({ name: "location_id" });
  const is_filler = useWatch({ name: "is_filler" });
  const sticky_position = useWatch({ name: "sticky_position" });
  const cell_span = useWatch({ name: "cell_span" });
  const target_url = useWatch({ name: "target_url" });
  const visibility_female_only = useWatch({ name: "visibility_female_only" });
  const visibility_logged_in_only = useWatch({
    name: "visibility_logged_in_only",
  });
  const visibility_not_logged_in_only = useWatch({
    name: "visibility_not_logged_in_only",
  });
  const visibility_speaker_id = useWatch({ name: "visibility_speaker_id" });
  const featured_speaker_ids = useWatch({ name: "featured_speaker_ids" });
  const frequency_cap_seconds = useWatch({ name: "frequency_cap_seconds" });
  const frequency_cap_storage = useWatch({ name: "frequency_cap_storage" });
  /* eslint-enable react-hooks/rules-of-hooks */

  const currentValues = {
    asset_id,
    location_id,
    is_filler,
    sticky_position,
    cell_span,
    target_url,
    visibility_female_only,
    visibility_logged_in_only,
    visibility_not_logged_in_only,
    visibility_speaker_id,
    featured_speaker_ids,
    frequency_cap_seconds,
    frequency_cap_storage,
  };

  return useMemo(() => {
    if (!record) return false;
    // Speaker ids arrive as an array, which never compares equal by identity.
    const norm = (v) => {
      if (Array.isArray(v))
        return v.filter((id) => id !== "none").join(",") || null;
      return v === undefined || v === "" ? null : v;
    };
    return CONTENT_FIELDS.some(
      (field) => norm(currentValues[field]) !== norm(record[field]),
    );
  }, [record, ...CONTENT_FIELDS.map((f) => currentValues[f])]);
};

// "End Now" button - sets ends_at to current timestamp and saves
const EndNowButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();

  if (!record) return null;

  // Only show if ends_at is null or in the future
  const endsAt = record.ends_at ? new Date(record.ends_at) : null;
  if (endsAt && endsAt < new Date()) return null;

  const handleClick = () => {
    const now = new Date().toISOString().slice(0, 19).replace("T", " ");
    update(
      "ad_placements",
      { id: record.id, data: { ends_at: now }, previousData: record },
      {
        onSuccess: () => {
          notify("Ad placement ended successfully!", { type: "success" });
          refresh();
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      },
    );
  };

  return (
    <Button
      variant="outlined"
      color="error"
      onClick={handleClick}
      disabled={isLoading}
      size="medium"
    >
      End Now
    </Button>
  );
};

/** Replace submits the same form as Save so validation and confirmation finish
 * before the current placement is ended. */
const ReplaceButton = () => {
  const isContentDirty = useContentFieldsDirty();
  const {
    formState: { isSubmitting },
  } = useFormContext();
  if (!isContentDirty) return null;
  return (
    <Button
      variant="outlined"
      color="primary"
      type="submit"
      name="action"
      value="replace"
      disabled={isSubmitting}
      size="medium"
    >
      Replace
    </Button>
  );
};

/** The placement form owns both save paths and checks image ratios at submission
 * time, including the default image fallback used on mobile by the website. */
export const AdPlacementForm = ({ isEdit = false }) => {
  const record = useRecordContext();
  const { save } = useSaveContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create, { isLoading: isCreating }] = useCreate();
  const [update, { isLoading: isUpdating }] = useUpdate();
  const [confirmation, setConfirmation] = useState(null);

  const submit = async (values, event) => {
    const replace = event?.nativeEvent?.submitter?.value === "replace";
    const data = {
      ...values,
      featured_speaker_ids: values.featured_speaker_ids.filter(
        (id) => id !== "none",
      ),
      visibility_speaker_id:
        values.visibility_speaker_id == null ||
        values.visibility_speaker_id === ""
          ? null
          : values.visibility_speaker_id,
    };
    try {
      const [{ data: asset }, { data: location }] = await Promise.all([
        dataProvider.getOne("ad_assets", { id: data.asset_id }),
        dataProvider.getOne("ad_locations", { id: data.location_id }),
      ]);
      let mismatch = false;
      let unverifiable = false;
      if (asset.type === "image") {
        for (const [url, dimensions] of [
          [asset.content_default, location.dimensions_default],
          [
            asset.content_mobile || asset.content_default,
            location.dimensions_mobile,
          ],
        ]) {
          if (!dimensions) continue;
          const match = dimensions.match(/^(\d+)x(\d+)$/);
          if (!match || Number(match[1]) <= 0 || Number(match[2]) <= 0) {
            unverifiable = true;
            continue;
          }
          const ratio = await new Promise((resolve) => {
            const image = new Image();
            const finish = (value) => {
              clearTimeout(timeout);
              image.onload = image.onerror = null;
              resolve(value);
            };
            // A broken or stalled image must not leave Save waiting indefinitely.
            const timeout = setTimeout(() => finish(null), 10000);
            image.onload = () =>
              finish(
                image.naturalHeight
                  ? image.naturalWidth / image.naturalHeight
                  : null,
              );
            image.onerror = () => finish(null);
            image.src = url || "";
          });
          if (ratio === null) unverifiable = true;
          else {
            /** Location dimensions describe one cell; spanning cells widens the
             * artwork at the same height, for both desktop and mobile checks. */
            const expected =
              (Number(match[1]) * (data.cell_span || 1)) / Number(match[2]);
            // Compare raw ratios; tolerate floating-point noise at exactly 10%.
            if (Math.abs(ratio - expected) / expected > 0.1 + Number.EPSILON)
              mismatch = true;
          }
        }
      }
      if (mismatch || unverifiable) {
        const confirmed = await new Promise((resolve) =>
          setConfirmation({
            resolve,
            message: mismatch
              ? "This asset’s aspect ratio differs from this location’s expected ratio by more than 10%. Save anyway?"
              : "Couldn’t verify this asset’s aspect ratio. Save anyway?",
          }),
        );
        if (!confirmed) return;
      }
      if (!replace) return await save(data);
      const now = new Date().toISOString().slice(0, 19).replace("T", " ");
      // Preserve the replacement sequence, but keep submission pending until
      // both writes finish so a second click cannot start another replacement.
      await update(
        "ad_placements",
        { id: record.id, data: { ends_at: now }, previousData: record },
        { returnPromise: true },
      );
      const newData = {};
      for (const field of CONTENT_FIELDS) {
        if (data[field] !== undefined) newData[field] = data[field];
      }
      newData.starts_at = now;
      newData.ends_at = data.ends_at || null;
      const newRecord = await create(
        "ad_placements",
        { data: newData },
        { returnPromise: true },
      );
      notify("Current placement ended and new placement created!", {
        type: "success",
      });
      redirect("edit", "ad_placements", newRecord.id);
    } catch (error) {
      notify(`Error: ${error.message}`, { type: "error" });
    }
  };

  const closeConfirmation = (confirmed) => {
    confirmation.resolve(confirmed);
    setConfirmation(null);
  };
  return (
    <>
      <SimpleForm
        record={
          record
            ? {
                ...record,
                featured_speaker_ids: record.featured_speaker_ids?.length
                  ? record.featured_speaker_ids
                  : ["none"],
              }
            : undefined
        }
        onSubmit={submit}
        toolbar={isEdit ? <EditToolbar /> : undefined}
      >
        <AdPlacementFormFields />
      </SimpleForm>
      <Confirm
        isOpen={!!confirmation}
        title="Check asset aspect ratio"
        content={confirmation?.message || ""}
        confirm="Save anyway"
        cancel="Cancel"
        loading={isCreating || isUpdating}
        onConfirm={() => closeConfirmation(true)}
        onClose={() => closeConfirmation(false)}
      />
    </>
  );
};

// Custom toolbar for the Edit view with End Now and Replace buttons
const EditToolbar = () => {
  return (
    <Toolbar sx={{ display: "flex", gap: 2 }}>
      <SaveButton />
      <EndNowButton />
      <ReplaceButton />
    </Toolbar>
  );
};

// Normalizes an ISO 8601 datetime string to MySQL format ("YYYY-MM-DD HH:mm:ss").
// If the value is already in MySQL format or is falsy, it is returned as-is.
const normalizeDateTimeForMysql = (value) => {
  if (!value) return value;
  // Already in MySQL format
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 19).replace("T", " ");
};

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      transform={(data) => ({
        ...data,
        starts_at: normalizeDateTimeForMysql(data.starts_at),
        ends_at: normalizeDateTimeForMysql(data.ends_at),
      })}
      mutationOptions={{
        onSuccess: () => {
          notify("Ad placement updated successfully!", { type: "success" });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <AdPlacementForm isEdit />
    </Edit>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      transform={(data) => {
        if (!data.starts_at) {
          data.starts_at = new Date()
            .toISOString()
            .slice(0, 19)
            .replace("T", " ");
        }
        return data;
      }}
      mutationOptions={{
        onSuccess: () => {
          notify("Ad placement created successfully!", { type: "success" });
          refresh();
          redirect("list", "ad_placements");
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <AdPlacementForm />
    </Create>
  );
}

export const AdPlacements = {
  category: "ads",
  name: "ad_placements",
  icon: Target,
  list: ListView,
  edit: EditView,
  create: CreateView,
  options: { label: "Ad Placements" },
};
